import React, { useEffect, useState } from "react";
import {
    Box,
    Chip,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
} from "@mui/material";
import styled from "styled-components";
import NotionSelectColors from "src/components/notion-colors";
import { format, parseISO } from "date-fns";

const StyledMenuItem = styled(MenuItem)`
    font-size: 12px;
`;

const CustomInputLabel = styled(InputLabel)`
    font-size: 12px;
    &.MuiInputLabel-shrink {
        padding-top: 3px;
    }
`;

const ChipLabel = styled.span`
    font-size: 12px;
`;

const DatabaseSetupTasks = ({
    userDatabases,
    selectedDatabase,
    setSelectedDatabases,
    setAllTaskFields,
    setAllBugTypes,
    setHasError,
    databasesFullPaths,
}) => {
    const requiredFields = ["status", "assignee", "ticket_type", "title"];
    if (!selectedDatabase.id) return null;

    const [idField, setIdField] = useState({});
    const [statusField, setStatusField] = useState({});
    const [typeField, setTypeField] = useState({});
    const [storyPointsField, setStoryPointsField] = useState({});
    const [timeSpentField, setTimeSpentField] = useState({});
    const [timeEstimatedField, setTimeEstimatedField] = useState({});
    const [assigneeField, setAssigneeField] = useState({});
    const [bugTypes, setBugTypes] = React.useState([]);

    const databaseTaskFields = [
        {
            id: "notion%3A%2F%2Ftasks%2Fauto_increment_id_property",
            name: "Task ID",
            acceptedTypes: ["unique_id"],
            adadotName: "id",
            value: idField,
            set: setIdField,
            databaseId: selectedDatabase.id,
        },
        {
            id: "notion%3A%2F%2Ftasks%2Fstatus_property",
            name: "Status",
            acceptedTypes: ["status", "select"],
            adadotName: "status",
            value: statusField,
            set: setStatusField,
            databaseId: selectedDatabase.id,
        },
        {
            id: undefined,
            name: "Story Points",
            acceptedTypes: ["number", "formula"],
            adadotName: "story_points",
            value: storyPointsField,
            set: setStoryPointsField,
            databaseId: selectedDatabase.id,
        },
        {
            id: "notion%3A%2F%2Ftasks%2Fassign_property",
            name: "Assignee",
            acceptedTypes: ["people"],
            adadotName: "assignee",
            value: assigneeField,
            set: setAssigneeField,
            databaseId: selectedDatabase.id,
        },
        {
            id: undefined,
            name: "Time Spent",
            acceptedTypes: ["number", "formula"],
            adadotName: "time_spent",
            value: timeSpentField,
            set: setTimeSpentField,
            databaseId: selectedDatabase.id,
        },
        {
            id: undefined,
            name: "Time Estimated",
            acceptedTypes: ["number", "formula"],
            adadotName: "time_estimated",
            value: timeEstimatedField,
            set: setTimeEstimatedField,
            databaseId: selectedDatabase.id,
        },
        {
            id: undefined,
            name: "Type",
            acceptedTypes: ["text", "select"],
            adadotName: "ticket_type",
            value: typeField,
            set: setTypeField,
            databaseId: selectedDatabase.id,
        },
    ];

    React.useEffect(() => {
        setAllTaskFields((prevFields) => {
            return [
                ...prevFields.map((prevField) => {
                    const editedField = databaseTaskFields.find(
                        (newField) =>
                            newField.adadotName === prevField.adadotName &&
                            newField.id === prevField.id &&
                            newField.databaseId === prevField.databaseId
                    );
                    if (editedField) return editedField;
                    return prevField;
                }),
                ...databaseTaskFields.filter(
                    (newField) =>
                        !prevFields.find(
                            (prevField) =>
                                newField.adadotName === prevField.adadotName &&
                                newField.id === prevField.id &&
                                newField.databaseId === prevField.databaseId
                        )
                ),
            ];
        });
    }, [
        bugTypes,
        assigneeField,
        storyPointsField,
        timeSpentField,
        timeEstimatedField,
        typeField,
        statusField,
        idField,
        setAllTaskFields,
    ]);

    const handleChange = (event) => {
        const {
            target: { value },
        } = event;
        setBugTypes(typeof value === "string" ? value.split(",") : value);
    };

    React.useEffect(() => {
        setAllBugTypes((prevBugTypes) => ({
            ...prevBugTypes,
            [selectedDatabase.id]: bugTypes,
        }));
    }, [bugTypes, selectedDatabase.id]);

    useEffect(() => {
        if (selectedDatabase && selectedDatabase.properties) {
            Object.values(selectedDatabase.properties).forEach((property) => {
                let matchedField = databaseTaskFields.find((field) => field.id === property.id);
                if (matchedField) {
                    matchedField.set(property);
                } else {
                    matchedField = databaseTaskFields.find(
                        (field) =>
                            field.name.toLowerCase() === property.name.toLowerCase() &&
                            field.acceptedTypes.includes(property.type)
                    );
                }
                if (matchedField) {
                    matchedField.set(property);
                }
            });
        }
    }, [selectedDatabase]);

    useEffect(() => {
        databaseTaskFields.forEach((field) => {
            if (!field.value.id && requiredFields.includes(field.adadotName)) {
                setHasError(true);
            } else if (setBugTypes && (!bugTypes || bugTypes.length === 0)) {
                setHasError(false);
            } else {
                setHasError(false);
            }
        });
    }, [selectedDatabase, bugTypes]);

    const ticketTypeField = databaseTaskFields.find((field) => field.adadotName === "ticket_type");

    const handleDatabaseChange = (e) => {
        const selected = userDatabases.find((db) => db.id === e.target.value);
        setSelectedDatabases(selected);
    };

    return (
        <Grid item style={{ marginTop: "40px" }}>
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <FormControl
                    variant="outlined"
                    margin="1"
                    size="small"
                    error={!selectedDatabase}
                    fullWidth
                >
                    <CustomInputLabel sx={{ fontSize: 12, paddingTop: "3px" }} id="database-select">
                        Database
                    </CustomInputLabel>
                    <Select
                        labelId="database-select"
                        label="Database"
                        value={selectedDatabase.id || ""}
                        sx={{ fontSize: 12 }}
                        onChange={handleDatabaseChange}
                    >
                        {userDatabases
                            .slice()
                            .sort((a, b) => {
                                const titleA =
                                    a.title[0]?.plain_text?.toLowerCase() || "untitled database";
                                const titleB =
                                    b.title[0]?.plain_text?.toLowerCase() || "untitled database";
                                return titleA.localeCompare(titleB);
                            })
                            .map((db) => (
                                <StyledMenuItem
                                    value={db.id}
                                    key={db.id}
                                    selected={db.id === selectedDatabase.id}
                                >
                                    {db.title[0]?.plain_text || "Untitled Database"}
                                    {db.title[0]?.plain_text && databasesFullPaths[db.id]
                                        ? ` - ${databasesFullPaths[db.id]}`
                                        : null}
                                    {db.created_time
                                        ? ` (created: ${format(
                                              parseISO(db.created_time),
                                              "d MMM yy"
                                          )})`
                                        : null}
                                </StyledMenuItem>
                            ))}
                    </Select>
                    {!selectedDatabase && <FormHelperText>Please select a database</FormHelperText>}
                </FormControl>
            </Stack>
            {selectedDatabase && selectedDatabase.properties && (
                <Grid
                    container
                    spacing={3}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{ marginTop: "10px" }}
                >
                    {databaseTaskFields.map((field) => {
                        return (
                            <Grid item key={field.name} width="100%">
                                <FormControl variant="outlined" margin="1" size="small" fullWidth>
                                    <CustomInputLabel
                                        sx={{ fontSize: 12 }}
                                        id={`${field.name}-select`}
                                    >
                                        {requiredFields.includes(field.adadotName)
                                            ? `${field.name}
                                        *`
                                            : `${field.name} `}
                                    </CustomInputLabel>
                                    <Select
                                        labelId={`${field.name}-select`}
                                        label={
                                            requiredFields.includes(field.adadotName)
                                                ? `${field.name}
                                        *`
                                                : `${field.name} `
                                        }
                                        value={field.value.id || ""}
                                        sx={{ fontSize: 12 }}
                                        onChange={(e) => {
                                            const selectedProp = Object.values(
                                                selectedDatabase.properties
                                            ).find((property) => property.id === e.target.value);
                                            field.set(selectedProp);
                                        }}
                                    >
                                        {Object.values(selectedDatabase.properties).map(
                                            (property) => (
                                                <StyledMenuItem
                                                    value={property.id}
                                                    key={property.id}
                                                    selected={property.id === field.value.id}
                                                    disabled={
                                                        !field.acceptedTypes.includes(property.type)
                                                    }
                                                >
                                                    <span>{property.name}</span>
                                                </StyledMenuItem>
                                            )
                                        )}
                                    </Select>
                                    {!field.value.id && requiredFields.includes(field.adadotName) && (
                                        <FormHelperText style={{ marginLeft: "3px" }} warning>
                                            * Selecting {field.name} is mandatory
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        );
                    })}
                    {ticketTypeField && ticketTypeField.value && ticketTypeField.value.name && (
                        <Grid item width="100%">
                            <FormControl variant="outlined" margin="1" size="small" fullWidth>
                                <CustomInputLabel id="bug-issue-types-label">
                                    Bug Issue Types*
                                </CustomInputLabel>
                                <Select
                                    labelId="bug-issue-types-label"
                                    id="bug-issue-types"
                                    multiple
                                    value={bugTypes}
                                    onChange={handleChange}
                                    input={
                                        <OutlinedInput
                                            id="bug-issue-types"
                                            label="Bug Issue Types"
                                        />
                                    }
                                    sx={{ fontSize: 12 }}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const fieldOptions =
                                                    selectedDatabase.properties[
                                                        ticketTypeField.value.name
                                                    ]?.select?.options || [];
                                                const field = fieldOptions.find(
                                                    (option) => option.id === value
                                                );
                                                return (
                                                    field && (
                                                        <Chip
                                                            key={field.id}
                                                            label={
                                                                <ChipLabel>{field.name}</ChipLabel>
                                                            }
                                                            size="small"
                                                            style={{
                                                                backgroundColor:
                                                                    NotionSelectColors[field.color],
                                                            }}
                                                        />
                                                    )
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {selectedDatabase.properties[
                                        ticketTypeField.value.name
                                    ]?.select?.options?.map(({ name, id, color }) => (
                                        <StyledMenuItem key={id} value={id}>
                                            <Box
                                                style={{
                                                    height: "16px",
                                                    width: "16px",
                                                    borderRadius: "4px",
                                                    marginRight: "6px",
                                                    backgroundColor: NotionSelectColors[color],
                                                }}
                                            />
                                            {name}
                                        </StyledMenuItem>
                                    ))}
                                </Select>
                                {(!bugTypes || bugTypes.length === 0) && (
                                    <FormHelperText error>
                                        Please select at least one bug issue type
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default DatabaseSetupTasks;
